// vendor libs
import ajax from 'app/common/lib/Ajax';
import AppVersion from 'app/common/lib/AppVersion';
import context from 'app/common/lib/context';
import FnUtil from 'app/common/lib/FnUtil';
import state from 'app/common/lib/state';

const LogToServer = {};

// 30 minutes
const MAX_TIMEOUT = 1000 * 60 * 30;
// by default, don't retry
const DEFAULT_NUM_RETRIES = 0;
const DELAY_FACTOR = 1.23;
const MAX_LOG_TO_SERVER_COUNT = 10;
const DEFAULT_RETRY_DELAY = 1;

LogToServer.BASE_URL_PATH = '/_/_/report/';
LogToServer.BASE_URL_PATH_TRACE = '/_/_/trace/';

/**
 * The type of the message that will be reported back to the server.
 *
 * @enum {string}
 */
LogToServer.SERVER_LOG_TYPE = {
  LOG: 'log',
  WARNING: 'warning',
  ERROR: 'error',
  PAGE_LOAD_METRIC: 'page_load_metric',
  INTERACTION_TIMING: 'interaction_timing',
  APP_METRIC: 'app_metric',
  BROWSER_EXTENSION: 'browser_extension',
  RENDER_TIME: 'render_time',
  NETWORK_LOAD: 'network_load',
  SIGNUP_LOAD: 'signup_load',
  TRACE: 'trace',
};

/**
 * HTTP methods that the logger may log
 *
 * @enum {string}
 */
LogToServer.HTTP_METHOD = {
  GET: 'get',
  POST: 'post',
};

/**
 * Strip out all sensitive fields for JSON.stringify
 * @param {string} key
 * @param {*} value
 */
const _stripSensitiveFields = function(key, value) {
  if (/password/i.test(key)) {
    return undefined;
  }
  return value;
};

/**
 * Log information to the server.
 *
 * @param {LogToServer.SERVER_LOG_TYPE} logType
 * @param {string} method Either 'post' or 'get'
 * @param {Object} logData JSON encodable data
 * @param {number=} logToServerTime (in ms) time when first attempt to log to server
 * @return {Promise<Object>}
 */
const _logToServer = function(logType, type, logData, logToServerTime) {
  let url;
  if (logType === 'trace') {
    url = LogToServer.BASE_URL_PATH_TRACE + logType + '/';
  } else {
    url = LogToServer.BASE_URL_PATH + logType + '/';
  }

  // Record the time difference between the first call attempt to the server and
  // when the call actually succeeds.
  if (logToServerTime) {
    logData.timeElapsed = Date.now() - logToServerTime;
  }

  if (logType) {
    logData.logType = logType;
  }

  const activeTarget = state.getActiveTarget();
  const user = require('app/common/lib/User').default;
  const envContext = {
    app_version: AppVersion.currentVersion,
    current_url: window.location.href,
    app_invalid: AppVersion.isNewVersionRequired(),
    page_load_request_identifier: context.initialPageContext.PAGE_LOAD_REQUEST_IDENTIFIER,
    active_target: activeTarget ? activeTarget.getFullName() : '',
    user: {
      advertiser_id: context.instance.advertiser ? context.instance.advertiser.id : '',
      unauth_id: context.instance.unauth_id ? context.instance.unauth_id : '',
      user_id: user.get('id') || '',
    },
  };

  const data = LogToServer._assembleXhrData(envContext, logData);
  return ajax({ url, type, data }).then(
    ({ ok, status, statusText }) =>
      ok
        ? Promise.resolve()
        : Promise.reject({
            type: 'error',
            message: statusText,
            httpStatus: status,
          }),
  );
};

LogToServer._assembleXhrData = function(envContext, logData) {
  let reportDataJson;

  try {
    reportDataJson = JSON.stringify(logData, _stripSensitiveFields);

    // TODO(chris): 2015-07-24 We're seeing "TypeError: Converting circular structure to JSON".
    // For now, catch the error so that the error reporting doesn't blow up and mask the root cause.
  } catch (err) {
    reportDataJson = JSON.stringify({
      messages: [err.toString()],
    });
  }

  const xhrData = {
    report_context: JSON.stringify(envContext, _stripSensitiveFields),
    report_data: reportDataJson,
  };

  return xhrData;
};

/**
 * Attempt to log the error to the server. If there is a failure and numRetries > 0,
 *     then recursively attempt to log to server.
 * @override
 * @param {LogToServer.SERVER_LOG_TYPE} logType
 * @param {LogToServer.HTTP_METHOD} method
 * @param {!Object} logData JSON encodable data
 * @param {number=} numRetries number of times to retry after failure to log to server
 * @param {number=} retryDelay delay (in ms) for the first retry
 * @param {number=} logToServerTime (in ms) time when first attempt to log to server
 */
LogToServer.logToServer = function(
  logType,
  method,
  logData,
  numRetries,
  retryDelay,
  logToServerTime,
) {
  numRetries = numRetries === undefined ? DEFAULT_NUM_RETRIES : numRetries;
  retryDelay = retryDelay === undefined ? DEFAULT_RETRY_DELAY : retryDelay;
  logToServerTime = logToServerTime === undefined ? undefined : retryDelay;

  _logToServer(logType, method, logData, logToServerTime).catch((error) => {
    if (numRetries) {
      numRetries -= 1;
      const newDelay = Math.min(MAX_TIMEOUT, retryDelay * DELAY_FACTOR);
      setTimeout(() => {
        LogToServer.logToServer(logType, method, logData, numRetries, newDelay, logToServerTime);
      }, retryDelay);
    } else {
      numRetries -= 1;
      const newLogData = {
        messages: ['Failed to log error'],
        logType,
        method,
        textStatus: error.type,
        serverErrorText: error.message,
        jqXhrStatus: error.httpStatus || 0,
      };
      _logToServer(LogToServer.SERVER_LOG_TYPE.ERROR, 'post', newLogData);
    }
  });
};

// Don't count performance logging into the max calls to logToServer.
LogToServer.logPerfDataToServer = LogToServer.logToServer;

// Limit the number of reports sent per page load.  This guards against one browser tab
// from flooding us with reports.
LogToServer.logToServer = FnUtil.maxCalls(LogToServer.logToServer, MAX_LOG_TO_SERVER_COUNT);

export default LogToServer;
